/**
 * ShopAccessGate — shop content and discovery require a Google account.
 *
 * Wraps /browse, /map, /shop/:id, /reviews/:id and the 3D viewer. Anonymous
 * visitors are sent to /login and returned to where they were headed once
 * signed in.
 *
 * This gate is a convenience, not the security boundary — it only decides what
 * the UI renders, and anyone can edit localStorage or call the API directly.
 * The real enforcement is server-side: /api/datasets, /api/shops/public,
 * /api/scan-data/* and the SAS token endpoints all reject non-Google sessions,
 * so a forged local state renders an empty shell and nothing more.
 */
import { useEffect, useState, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { apiUrl, refreshBlobDirectAccess } from '../../config';

/**
 * Phone and device accounts carry an explicit authType; Google accounts
 * created before that field existed have none, hence the default. The id
 * prefix is checked independently so a hand-edited authType cannot pass.
 * Mirrors is_google_user() in services/api/auth.py.
 */
function isGoogleAccount(user: { id?: string; authType?: string } | null): boolean {
  if (!user) return false;
  if ((user.authType ?? 'google') !== 'google') return false;
  const id = String(user.id ?? '');
  return !id.startsWith('phone_') && !id.startsWith('device_');
}

export default function ShopAccessGate({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const setAuth = useAuthStore((s) => s.setAuth);
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (token) {
      setChecking(false);
      return;
    }

    const stored = localStorage.getItem('dukkan_token');
    if (!stored) {
      setChecking(false);
      return;
    }

    // Validate against the server rather than trusting the stored token —
    // /api/auth/me is what decides whether this session is real.
    fetch(apiUrl('/api/auth/me'), {
      headers: { Authorization: `Bearer ${stored}` },
    })
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error('expired');
      })
      .then((u) => {
        setAuth(u, stored);
        // The blob SAS tokens now need a session; re-fetch them under this one.
        refreshBlobDirectAccess();
      })
      .catch(() => {
        localStorage.removeItem('dukkan_token');
      })
      .finally(() => setChecking(false));
  }, [token, setAuth]);

  if (checking) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg-primary)',
          color: 'var(--text-secondary)',
        }}
      >
        Loading...
      </div>
    );
  }

  if (!token || !isGoogleAccount(user)) {
    // Remember the destination so login can return the visitor to it.
    localStorage.setItem(
      'dukkan_redirect_after_login',
      location.pathname + location.search,
    );
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
